/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

// Utils
import Select, { components } from 'react-select';
import { useTranslation } from 'react-i18next';
import { last, initial } from 'lodash';
import cn from '../../../../utils/tools/cn';

// Icons
import { ReactComponent as ChevronDown } from '../../../../assets/icons/chevron_down.svg';
import { ReactComponent as ClearCross } from '../../../../assets/icons/cross.svg';

import styles from './CustomSelect.module.scss';

function DropdownIndicator(props) {
  return (
    components.DropdownIndicator && (
      <components.DropdownIndicator {...props}>
        <ChevronDown width={12} height={6} />
      </components.DropdownIndicator>
    )
  );
}

function ClearIndicator(props) {
  return (
    components.ClearIndicator && (
      <components.ClearIndicator {...props}>
        <ClearCross width={16} height={16} />
      </components.ClearIndicator>
    )
  );
}

function Option({ label, data, ...props }) {
  return (
    components.Option && (
      <components.Option {...props}>
        <div className="d-flex lh-light">
          <span className="select-field__option-radio" />

          {data?.icon ? (
            <span className="d-flex center m-r-10">{data?.icon}</span>
          ) : null}

          {label}
        </div>
      </components.Option>
    )
  );
}

function ValueContainer({ children, ...props }) {
  return (
    components.ValueContainer && (
      <components.ValueContainer {...props}>
        {props.isMulti && props.getValue()?.length ? (
          <div className="multi-value-container">
            <span className="value-holder">{props.getValue()[0].label}</span>

            {props.getValue()?.length > 1 ? (
              <span className="select-field__multi-counter">
                {`+ ${props.getValue().length - 1}`}
              </span>
            ) : null}
          </div>
        ) : (
          initial(children)
        )}
        {last(children)}
      </components.ValueContainer>
    )
  );
}

function MenuList({
  children, options, setValue, selectProps, ...props
}) {
  const { t } = useTranslation();
  const { allowSelectAll, isMulti, value } = selectProps;

  const handleSelectAll = () => {
    if (!allowSelectAll || !isMulti) return;
    setValue(value?.length === options.length ? [] : options);
  };

  return (
    components.MenuList && (
      <components.MenuList {...props}>
        {allowSelectAll && isMulti && options?.length > 0 ? (
          <button
            type="button"
            className={cn([styles.select_all, 'hover-bright'])}
            onClick={() => handleSelectAll()}
          >
            {value?.length === options.length
              ? t('common.select.unselectAll')
              : `${t('common.select.selectAll')} (${options.length})`}
          </button>
        ) : null}

        {children}
      </components.MenuList>
    )
  );
}

function CustomSelect({
  noLabel, noRightBorder, isMulti, disabled, closeOnSelect, clearable, menuPosition, ...props
}) {
  const { t } = useTranslation();
  const ref = useRef();

  const [isOpened, setIsOpened] = useState(false);

  const handleClickOutside = (e) => {
    e.stopPropagation();
    setIsOpened(ref?.current?.controlRef?.contains(e.target));
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <Select
      ref={ref}
      className={cn([
        'select-field',
        noLabel ? ' select-with-no-label' : '',
        props.value?.value === undefined ? 'select-with-undefined' : '',
      ])}
      classNamePrefix="select-field"
      menuPortalTarget={document.querySelector('#root')}
      components={{
        DropdownIndicator, ClearIndicator, ValueContainer, Option, MenuList,
      }}
      noOptionsMessage={() => t('common.noResult')}
      isMulti={isMulti}
      hideSelectedOptions={false}
      isDisabled={disabled}
      closeMenuOnSelect={closeOnSelect}
      menuIsOpen={isOpened}
      onMenuOpen={() => setIsOpened(true)}
      onMenuClose={() => setIsOpened(false)}
      isOptionDisabled={(option) => option.disabled}
      isClearable={clearable}
      menuPosition={menuPosition || 'absolute'}
      onInputChange={() => {
        if (isMulti) {
          window.scrollBy(0, props?.value?.length && props.value.length % 2 === 0 ? -1 : 1);
        }
      }}
      {...props}
    />
  );
}

Option.propTypes = {
  label: PropTypes.string,
  data: PropTypes.shape({ icon: PropTypes.node }),
};

Option.defaultProps = {
  label: '',
  data: null,
};

ValueContainer.propTypes = {
  children: PropTypes.node,
  isMulti: PropTypes.bool,
  getValue: PropTypes.func,
  selectProps: PropTypes.shape({
    value: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.shape({})),
      PropTypes.shape({}),
    ]),
  }),
};

ValueContainer.defaultProps = {
  children: null,
  isMulti: false,
  getValue: () => {},
  selectProps: null,
};

MenuList.propTypes = {
  children: PropTypes.node,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  setValue: PropTypes.func,
  selectProps: PropTypes.shape({
    allowSelectAll: PropTypes.bool,
    isMulti: PropTypes.bool,
    value: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.shape({})), PropTypes.shape({})]),
  }),
};

MenuList.defaultProps = {
  children: null,
  options: [],
  setValue: () => {},
  selectProps: {
    allowSelectAll: false,
    isMulti: false,
    value: [],
  },
};

CustomSelect.propTypes = {
  noLabel: PropTypes.bool,
  noRightBorder: PropTypes.bool,
  isMulti: PropTypes.bool,
  disabled: PropTypes.bool,
  closeOnSelect: PropTypes.bool,
  clearable: PropTypes.bool,
  menuPosition: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape({})),
    PropTypes.shape({}),
  ]),
  options: PropTypes.arrayOf(PropTypes.shape({})),
  allowSelectAll: PropTypes.bool,
};

CustomSelect.defaultProps = {
  noLabel: false,
  noRightBorder: false,
  isMulti: false,
  disabled: false,
  closeOnSelect: true,
  clearable: true,
  menuPosition: 'absolute',
  value: null,
  options: [],
  allowSelectAll: false,
};

export default CustomSelect;
